import type { Entity, LoadResult, OtherCostMonthDetail, OtherCostMonthRow } from "./api";

export const ENTITY_NAME: Record<Entity, string> = { LTD: "Ketosource Ltd (UK)", OU: "Ketosource OÜ (EU)", LLC: "Ketosource MENA (UAE)" };
export const ENTITY_CURRENCY: Record<Entity, string> = { LTD: "GBP", OU: "EUR", LLC: "AED" };

/** Where the month's account totals came from: the Xero P&L view (with the day of the last load) or typed by hand. */
export function sourceLabel(row: OtherCostMonthRow): string {
  if (row.source !== "table") return "Typed";
  return row.loaded_at ? `From Xero ${row.loaded_at.slice(0, 10)}` : "From Xero";
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The sentence shown after a load: what was written, whether Xero still reloads the month, and the two gaps the operator may act on. */
export function loadSummary(result: LoadResult): string {
  const asOf = result.view_loaded_at ? ` (Xero figures as of ${result.view_loaded_at.slice(0, 10)})` : "";
  const parts = [`Loaded ${plural(result.rows, "account total", "account totals")} from Xero for ${ENTITY_NAME[result.entity_id]}, ${monthLabel(result.month)}${asOf}.`];
  if (result.still_reloading) parts.push("Xero is still reloading this month; load it again before you close it.");
  if (result.unseeded.length) parts.push(`${plural(result.unseeded.length, "account needs", "accounts need")} a scope: ${result.unseeded.join(", ")}.`);
  if (result.typed_only.length) parts.push(`${plural(result.typed_only.length, "typed account", "typed accounts")} Xero does not carry ${result.typed_only.length === 1 ? "stays" : "stay"} as typed: ${result.typed_only.join(", ")}.`);
  return parts.join(" ");
}

export function statusLabel(row: OtherCostMonthRow): "Closed" | "Overdue" | "Open" {
  if (row.status === "closed") return "Closed";
  return row.overdue ? "Overdue" : "Open";
}

/** Why Confirm close is disabled, or null when it may be pressed. The engine enforces the same rule (409 close_blocked). */
export function closeBlockedReason(detail: OtherCostMonthDetail | undefined): string | null {
  if (!detail) return "Loading the month";
  if (detail.close?.status === "closed") return "Already closed";
  if (detail.typed.length === 0) return "No account totals entered yet";
  if (detail.unmapped_accounts.length) return `${detail.unmapped_accounts.length} account${detail.unmapped_accounts.length === 1 ? "" : "s"} without a scope`;
  const open = detail.flags.filter((flag) => flag.status === "open").length;
  if (open) return `${open} tie-out flag${open === 1 ? "" : "s"} open`;
  return null;
}

/** The last `count` month starts, newest first, as YYYY-MM-01 (today's month included). */
export function monthOptions(today: Date, count = 12): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const date = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i, 1));
    out.push(date.toISOString().slice(0, 10));
  }
  return out;
}

export function monthLabel(month: string): string {
  const [year, value] = month.split("-").map(Number);
  return new Date(Date.UTC(year, value - 1, 1)).toLocaleString("en-GB", { month: "short", year: "2-digit", timeZone: "UTC" });
}

/** Parse pasted totals: `name<TAB or ;>amount[<TAB or ;>COGS|CHANNEL]`. */
export function parseAccountLines(text: string, defaultGroup: "COGS" | "CHANNEL" = "CHANNEL"): { rows: Array<{ account_name: string; amount: string; account_group: "COGS" | "CHANNEL" }>; refused: string[] } {
  const rows: Array<{ account_name: string; amount: string; account_group: "COGS" | "CHANNEL" }> = [];
  const refused: string[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(/\t|;/).map((part) => part.trim());
    const amount = (parts[1] ?? "").replace(/[,£€\s]/g, "").replace(/^AED/i, "");
    const group = (parts[2] ?? "").toUpperCase();
    if (parts.length < 2 || !/^-?\d+(\.\d{1,4})?$/.test(amount)) { refused.push(line); continue; }
    rows.push({ account_name: parts[0], amount, account_group: group === "COGS" ? "COGS" : group === "CHANNEL" ? "CHANNEL" : defaultGroup });
  }
  return { rows, refused };
}
